// configuration.config.gavion.* → typed runtime config (spec 02 §4).
import { EMAIL_RE } from "./consent.ts";
import { isHttpsUrl } from "./render.ts";
import { CASL_ORG } from "./types.ts";
import { quebecHolidaysAround } from "./quebecHolidays.ts";

export type EmailRuntimeConfig = {
  sendingDomain: string;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  contactEmail: string;
  postalAddress: string;
  dailyCap: number;
  /**
   * GAVION campaign-fixes: sequence emails per sending rep (mailbox) per Toronto day,
   * gavion.email.mailbox_daily_cap (a positive integer), else MAILBOX_DAILY_CAP. The org cap (dailyCap) still applies.
   */
  mailboxDailyCap: number;
  logoUrl: string;
  businessDays: readonly number[];
  /**
   * gavion.holidays when it is an array (an admin override, even empty; invalid dates dropped), else the
   * Quebec statutory holidays around now (quebecHolidays.ts; SQL twin private.email_holidays()).
   */
  holidays: readonly string[];
  privacyOfficer: Readonly<{ name: string | null; email: string | null }>;
  /** GAVION open-pixel: gavion.email.open_tracking; only an explicit false turns it off. */
  openTracking: boolean;
  /**
   * GAVION missed-call: gavion.ai.brochure_url (Paramètres → IA, « Lien de la brochure »), https only, else "".
   * Template variable {{brochure_url}} (a template wraps it in {{#if brochure_url}} so an empty link hides).
   */
  brochureUrl: string;
};

/** GAVION campaign-fixes: default per-mailbox daily cap of sequence emails (owner OK 2026-10-28). */
export const MAILBOX_DAILY_CAP = 40;

export const EMAIL_CONFIG_DEFAULTS: EmailRuntimeConfig = Object.freeze({
  sendingDomain: "mail.gavion.ai",
  fromName: "Gavion",
  fromEmail: "info@mail.gavion.ai",
  replyTo: "sales@gavion.ai",
  contactEmail: CASL_ORG.contactEmail,
  postalAddress: CASL_ORG.postalAddress,
  dailyCap: 500,
  mailboxDailyCap: MAILBOX_DAILY_CAP,
  logoUrl: "https://crm.gavion.ai/email/gavion-logo.png",
  businessDays: Object.freeze([1, 2, 3, 4, 5]),
  holidays: Object.freeze(quebecHolidaysAround(new Date())),
  privacyOfficer: Object.freeze({ name: null, email: null }),
  openTracking: true,
  brochureUrl: "",
}) as EmailRuntimeConfig;

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Obj) : {};
const str = (v: unknown, d: string): string =>
  typeof v === "string" ? v.trim() : d;
const nonEmpty = (v: unknown, d: string): string => str(v, d) || d;
const optional = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** YYYY-MM-DD that names a real calendar day (rejects 2026-02-30, 2026-13-01). */
const isIsoDay = (h: unknown): h is string => {
  if (typeof h !== "string") return false;
  const m = ISO_DAY.exec(h);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === mo - 1 &&
    dt.getUTCDate() === d
  );
};
const contactEmailOf = (v: unknown, d: string): string => {
  const s = str(v, "");
  return EMAIL_RE.test(s) ? s : d;
};
/** GAVION missed-call: an https brochure link, else "" (same rule as Paramètres → IA and the AI context). */
const brochureUrlOf = (v: unknown): string => {
  const s = str(v, "");
  return isHttpsUrl(s) ? s : "";
};
const isWeekday = (d: unknown): boolean =>
  Number.isInteger(d) && (d as number) >= 1 && (d as number) <= 7;

export function resolveEmailConfig(
  configurationConfig: unknown,
  now: Date = new Date(),
): EmailRuntimeConfig {
  const g = obj(obj(configurationConfig).gavion);
  const e = obj(g.email);
  const D = EMAIL_CONFIG_DEFAULTS;
  const po = obj(g.privacy_officer);
  const logo = nonEmpty(e.logo_url, D.logoUrl);
  const days = g.business_days;
  return {
    sendingDomain: nonEmpty(e.sending_domain, D.sendingDomain),
    fromName: nonEmpty(e.from_name, D.fromName),
    fromEmail: nonEmpty(e.from_email, D.fromEmail),
    replyTo: nonEmpty(e.reply_to, D.replyTo),
    contactEmail: contactEmailOf(e.contact_email, D.contactEmail),
    // A blank postal address stays blank so scheduling can refuse it (CASL).
    postalAddress: str(e.postal_address, D.postalAddress),
    dailyCap:
      Number.isInteger(e.daily_cap) && (e.daily_cap as number) > 0
        ? (e.daily_cap as number)
        : D.dailyCap,
    mailboxDailyCap:
      Number.isInteger(e.mailbox_daily_cap) &&
      (e.mailbox_daily_cap as number) > 0
        ? (e.mailbox_daily_cap as number)
        : D.mailboxDailyCap,
    // Cosmetic values degrade to the default; CASL values (postal address) fail loud instead.
    // Carry: the settings UI (T13+) must validate logo_url as https on save.
    logoUrl: isHttpsUrl(logo) ? logo : D.logoUrl,
    businessDays:
      Array.isArray(days) && days.length > 0 && days.every(isWeekday)
        ? [...(days as number[])]
        : [...D.businessDays],
    holidays: Array.isArray(g.holidays)
      ? g.holidays.filter(isIsoDay)
      : quebecHolidaysAround(now),
    privacyOfficer: { name: optional(po.name), email: optional(po.email) },
    openTracking: e.open_tracking !== false,
    brochureUrl: brochureUrlOf(obj(g.ai).brochure_url),
  };
}

/** True only for an address directly on the configured sending domain (no subdomains). */
export const isSendingAddress = (
  email: string,
  cfg: EmailRuntimeConfig,
): boolean => {
  const at = email.trim().toLowerCase().split("@");
  return (
    at.length === 2 && at[0] !== "" && at[1] === cfg.sendingDomain.toLowerCase()
  );
};
